import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  SelectChangeEvent,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  MiscellaneousServicesOutlined as SettingIcon,
  VisibilityOutlined as DashboardIcon,
  DangerousOutlined as StopCircleIcon,
  DescriptionOutlined as ReportIcon,
  Map as MapIcon,
  MapOutlined as MapOutlinedIcon,
  DragHandle as DragHandleIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  LockOutlined as LockIcon,
  LockOpenOutlined as LockOpenIcon,
  ArchiveOutlined as ArchiveIcon,
  UnarchiveOutlined as RestoreIcon,
  DeleteOutlined as DeleteIcon,
  ClearOutlined as RemoveIcon,
  FileCopyOutlined as DuplicateIcon,
  ViewCompactOutlined as CompactViewIcon,
  ViewAgendaOutlined as DetailedViewIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { poursAPI as pourAPI, DashboardResponse } from '../services';
import type { Pour2Limit, PourMapSummary } from '../services/pours';
import projectsService, { type ProjectListItem } from '../services/projects';
import ReportDialog from '../components/ReportDialog';
import { MaturityProgress } from '../components/dashboard/MaturityProgress';
import {
  TemperatureProfileBar,
  DEFAULT_SCALE_MAX,
  type TempLimitMarker,
} from '../components/dashboard/TemperatureProfileBar';
import { StatusGlyph, loggerLevel } from '../components/dashboard/StatusGlyph';
import { locationHasMaturity } from '../utils/locationMaturity';
import EmptyState from '../components/EmptyState';
import { apiErrorText } from '../utils/apiError';
import { isLoggingEnded, locationOnlineStatus } from '../utils/onlineStatus';
import { functionalityLabel } from '../utils/pourFunctionality';
import { formatInTimeZone, formatDateShort, timeZoneLabel } from '../utils/datetime';
import { pastPlannedEnd, plannedEndSentence } from '../utils/pastPlannedEnd';
import { useAuth } from '../context/AuthContext';
import { hasPermission, isSuperAdmin } from '../utils/userPermissions';
import { companiesAPI, type Company } from '../services/companies';
import { CloudOff as CloudOffIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { useMapView } from '../context/MapViewContext';
import {
  isHumidityLocation,
  isSiteConditionsChannel,
  orderByFunction,
} from '../utils/locationChannels';
import { SiteWeatherLine } from '../components/dashboard/SiteWeather';
import { linkedSiteWeather } from '../utils/siteConditions';
import {
  formatHalfUp,
  formatMaturity,
  formatMaturityWithIndex,
  formatStrengthReading,
  maturityMeta,
  maturityShortTitle,
  pourHasCurve,
  resolveStrengthReading,
  resolveStrengthUnit,
  strengthReadingTooltip,
} from '../utils/maturityDisplay';
import { OutsideRangeTargets, StrengthTip, tipUnderline } from '../components/dashboard/StrengthDisplay';
import { useIsMobile } from '../utils/useIsMobile';
import MobileControlPanel from '../components/controlPanel/MobileControlPanel';

/** Map a pour's temperature limits (Criteria '02') to bar markers. */
const tempMarkersFromLimits = (limits: Pour2Limit[]): TempLimitMarker[] =>
  limits
    .filter((l) => l.Criteria === '02')
    .map((l) => ({
      temp: Number(l.LimitsTemp),
      color: l.BackgroundColor || undefined,
      label: l.Purpose || undefined,
    }));

/** Bar scale max from the group's "Temperature Bar Indicator" row (Criteria
 *  '03'); falls back to the default (110) when none is configured. */
const scaleMaxFromLimits = (limits: Pour2Limit[]): number => {
  const bar = limits.find((l) => l.Criteria === '03');
  const v = bar ? Number(bar.LimitsTemp) : NaN;
  return v > 0 ? v : DEFAULT_SCALE_MAX;
};

/** Simple 0-100% fill bar for humidity channels (the temperature profile
 *  gradient is meaningless for relative humidity). Mirrors LocationList. */
const HumidityBar: React.FC<{ value?: number | null; height?: number }> = ({
  value,
  height = 22,
}) => (
  <Box
    sx={{
      position: 'relative',
      height,
      borderRadius: 1,
      backgroundColor: 'grey.200',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: `${Math.min(Math.max(value ?? 0, 0), 100)}%`,
        backgroundColor: '#4a90c2',
        transition: 'width 0.3s',
      }}
    />
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        right: 6,
        top: '50%',
        transform: 'translateY(-50%)',
        fontWeight: 600,
        color: 'text.secondary',
      }}
    >
      {value != null ? `${value.toFixed(1)} %RH` : '-'}
    </Typography>
  </Box>
);

// Fix default marker icons for leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const projectIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="#1565C0" stroke="#FFFFFF" stroke-width="1.5"/>
      <circle cx="12.5" cy="12.5" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

/** Force Leaflet to recalculate its size when the container dimensions change */
const InvalidateSize: React.FC<{ height: number }> = ({ height }) => {
  const map = useMap();
  useEffect(() => {
    const timeout = setTimeout(() => {
      // Bail if the map was torn down before the delay elapsed
      if ((map as any)._mapPane) map.invalidateSize();
    }, 350);
    return () => clearTimeout(timeout);
  }, [map, height]);
  return null;
};

/** Zoom in on a single point (e.g. the latest project) instead of fitting all
 *  markers. Keyed on the coordinates, not the array: the page builds a new
 *  array on every render (each poll renders), and re-focusing then would
 *  undo the user's own pan and zoom. */
const FocusPosition: React.FC<{ position: [number, number] | null; zoom?: number }> = ({ position, zoom = 14 }) => {
  const map = useMap();
  const lat = position?.[0];
  const lng = position?.[1];
  useEffect(() => {
    if (lat == null || lng == null) return;
    const timeout = setTimeout(() => {
      // Bail if the map was torn down before the delay elapsed
      if ((map as any)._mapPane) map.setView([lat, lng], zoom);
    }, 400);
    return () => clearTimeout(timeout);
  }, [map, lat, lng, zoom]);
  return null;
};

interface Pour {
  PourID: string;
  PourCode?: string;
  PourName: string;
  ProjectName?: string;
  ProductCode?: string;
  Functionality?: string;
  /** Comma-joined granular monitoring functions; preferred over Functionality
   *  for the displayed label (T&H pours derive legacy code '2'). */
  MonitoringFunctions?: string;
  Location?: string;
  PourVolume?: number;
  PourDate?: string;
  PourStatus?: string;
  CompanyName?: string;
  LoggerStart?: string;
  LoggerDuration?: number;
  TimeZone?: string;
  Latitude?: number;
  Longitude?: number;
  // Health roll-up summary — populated by the pours list API (backend slice #1).
  // Undefined until that lands, so the health row simply doesn't render yet.
  WarningCount?: number;
  AnyStale?: boolean;
  LoggerCount?: number;
  /** In Progress but not started yet: every location starts in the future
   *  and no reading has arrived. Shown as "Scheduled" with StartsAt. */
  Scheduled?: boolean;
  /** The earliest planned start (naive UTC) of a Scheduled pour. */
  StartsAt?: string;
  /** The pour's weather station (site weather, not counted in LoggerCount
   *  or AnyStale) should be reporting and is not. */
  HasWeatherStation?: boolean;
  WeatherStationStale?: boolean;
  /** Completed pours auto-lock; admins can unlock/re-lock. */
  Locked?: boolean;
  [key: string]: any;
}

/** The control panel refreshes the list and the open row this often, while
 *  the page is visible. */
const POLL_MS = 5 * 60 * 1000;

/** How long the search box waits after the last key before asking the
 *  server. */
const SEARCH_DEBOUNCE_MS = 300;

/** A row's cached quick view and when it was fetched. */
interface CachedDashboard {
  data: DashboardResponse;
  fetchedAt: number;
}

/** A cached quick view younger than the poll interval: still current. An
 *  older one (a row hovered or opened long ago) would show an old last
 *  read and maturity, so it is reloaded before it is trusted. */
const isFreshEntry = (entry: CachedDashboard | undefined): boolean =>
  !!entry && Date.now() - entry.fetchedAt < POLL_MS;

/** Whether the pour shows as Scheduled: In Progress (Status stays '1' for
 *  legacy) but not started yet. */
const isScheduledPour = (pour: Pour): boolean =>
  !!pour.Scheduled && (pour.PourStatus ?? '').toLowerCase() === 'in progress';

/** The status shown for a pour: "Scheduled" before its start, else its own. */
const displayStatus = (pour: Pour): string =>
  isScheduledPour(pour) ? 'Scheduled' : pour.PourStatus || '';

const HEADER_CELL_SX = { fontWeight: 'bold', backgroundColor: 'grey.50', whiteSpace: 'nowrap' } as const;

// DEMO FLAG: list-level readability: "Started 21 Aug 2026, 10:30" and
// "Age 648 h" columns, timezone folded into the Started heading when every
// pour shares one. Set false to revert.
const LIST_CLARITY = true;

// The quick-view panel has two layouts the user can flip between with the
// small switch in its top-right corner (remembered per browser):
//   detailed: labelled action strip, limit legend, Latest / Final heading,
//             labels in empty bars, darker group headings, filled profile
//             toggle (both layouts show "Logging ended" once logging ended);
//   compact:  the original icon-only strip and bare bars.
// Backup of the pre-change file lives in .demo-backups/ at the repo root.
const PANEL_VIEW_STORAGE_KEY = 'conxedge.pourPanelView';
const readStoredPanelView = (): boolean => {
  try {
    return localStorage.getItem(PANEL_VIEW_STORAGE_KEY) !== 'compact';
  } catch {
    return true;
  }
};

const DesktopPours: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  // Lock/Unlock/Archive/Delete/Restore/Duplicate are admin-role actions (UI Development doc 27.07)
  const canManagePours = hasPermission(user, 'pours.create');

  const [pours, setPours] = useState<Pour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  // Mobile: search is collapsed to an icon by default so the status chips keep
  // the full row width; tapping it expands an inline search field.
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('In progress');
  // Superadmin only: narrow the list to one client ('' = every client).
  const userIsSuper = isSuperAdmin(user);
  const [clientFilter, setClientFilter] = useState<string>('');
  const [clients, setClients] = useState<Company[]>([]);
  // Live In Progress pours per client id: clients with some are listed first.
  const [clientCounts, setClientCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!userIsSuper) return;
    companiesAPI
      .getCompanies()
      .then((list) =>
        setClients([...list].sort((a, b) => (a.companyname || '').localeCompare(b.companyname || ''))),
      )
      .catch(() => {
        // Non-critical: the list stays unfiltered.
      });
    pourAPI
      .getClientInProgressCounts()
      .then(setClientCounts)
      .catch(() => {
        // Non-critical: the clients stay in name order.
      });
  }, [userIsSuper]);
  const activeClients = clients.filter((c) => (clientCounts[c.companyid] ?? 0) > 0);
  const otherClients = clients.filter((c) => !(clientCounts[c.companyid] ?? 0));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Map state. On mobile the open/close control lives in the bottom nav, so the
  // state is shared via context; on desktop the inline bar toggles it locally.
  const mapView = useMapView();
  const [localShowMap, setLocalShowMap] = useState(true);
  const showMap = isMobile ? mapView.open : localShowMap;
  const [mapSize, setMapSize] = useState<'small' | 'medium' | 'large' | 'custom'>('medium');
  const [customMapHeight, setCustomMapHeight] = useState(350);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  // Project markers state
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  // Tracks whether the projects fetch has finished, so the map can wait for the
  // focus target before mounting (react-leaflet reads center/zoom only at mount).
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  // Expandable row state
  const [expandedPourId, setExpandedPourId] = useState<string | null>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [expandedError, setExpandedError] = useState<string>('');
  // The open row, for requests that answer after the user moved on.
  const expandedPourIdRef = useRef(expandedPourId);
  expandedPourIdRef.current = expandedPourId;
  const dashboardCache = useRef<Record<string, CachedDashboard>>({});
  const limitsCache = useRef<Record<string, Pour2Limit[]>>({});
  // Bumped when limits land for an already-expanded pour, since the ref cache
  // alone would not trigger a re-render of the (already drawn) profile bars.
  const [, setLimitsVersion] = useState(0);
  // Bumped when new data replaces the open row's cached dashboard, for the
  // same reason.
  const [, setDashboardVersion] = useState(0);
  // Rows whose quick view is loading in the background (hover, or a stale
  // entry reloading), so a load is never started twice.
  const prefetchingRef = useRef<Set<string>>(new Set());
  const [expandedViewMode, setExpandedViewMode] = useState<'temperature' | 'strength'>('temperature');

  // Stop-pour confirmation dialog state. `stopTargetPour` holds the row
  // chosen by the user; `stoppingPourId` blocks the icon during the API call.
  const [stopTargetPour, setStopTargetPour] = useState<Pour | null>(null);
  const [stoppingPourId, setStoppingPourId] = useState<string | null>(null);
  const [stopError, setStopError] = useState<string>('');
  const [stopSuccess, setStopSuccess] = useState<string>('');

  // Lock / unlock / archive / restore (quick-view panel actions)
  const [lockBusyPourId, setLockBusyPourId] = useState<string | null>(null);
  const [archiveTargetPour, setArchiveTargetPour] = useState<Pour | null>(null);
  const [archivingPour, setArchivingPour] = useState(false);
  const [restoringPourId, setRestoringPourId] = useState<string | null>(null);
  // 'Archived' is a separate server-side list (soft-deleted pours), not a status.
  const showArchived = statusFilter === 'Archived';
  // Detailed vs compact quick-view panel (see PANEL_VIEW_STORAGE_KEY).
  const [detailedView, setDetailedView] = useState<boolean>(readStoredPanelView);
  const toggleDetailedView = () => {
    setDetailedView((v) => {
      const next = !v;
      try { localStorage.setItem(PANEL_VIEW_STORAGE_KEY, next ? 'detailed' : 'compact'); } catch { /* ignore */ }
      return next;
    });
  };
  const LABELLED_ACTION_ROW = detailedView;
  const PANEL_CLARITY = detailedView;
  // Report generation dialog: holds the pour whose report options are being
  // chosen; success/error feedback is surfaced via a snackbar.
  const [reportDialogPour, setReportDialogPour] = useState<Pour | null>(null);
  const [reportMessage, setReportMessage] = useState<string>('');
  // null = unknown/loading; number = count of tank-bound locations on the
  // selected pour. Drives whether the dialog warns about tank reset.
  const [stopTankCount, setStopTankCount] = useState<number | null>(null);

  const openStopDialog = useCallback(async (pour: Pour) => {
    setStopTargetPour(pour);
    setStopTankCount(null);
    try {
      const locations = await pourAPI.getPourLocations2(pour.PourID);
      const count = locations.filter(
        (loc) => loc.TankID != null && loc.TankID !== ''
      ).length;
      setStopTankCount(count);
    } catch {
      setStopTankCount(0);
    }
  }, []);

  const mapHeights = { small: 200, medium: 350, large: 550, custom: customMapHeight };

  // Drag-to-resize handlers
  const currentMapHeight = mapHeights[mapSize];
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartHeight.current = currentMapHeight;

    const handleDragMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientY - dragStartY.current;
      const newHeight = Math.max(150, Math.min(800, dragStartHeight.current + delta));
      setCustomMapHeight(newHeight);
      setMapSize('custom');
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, [currentMapHeight]);

  // The server filters, searches and pages the list (a superadmin can see
  // tens of thousands of pours): `pours` is the page shown, `totalCount`
  // the pours matching the filters, `totalAll` the list before them.
  const [totalCount, setTotalCount] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  // The search box asks the server once typing pauses, not on every key. A
  // new search starts on the first page, in the same update, so the old
  // page is never requested for it.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debouncedSearchRef = useRef('');
  useEffect(() => {
    const term = searchFilter.trim();
    const timer = window.setTimeout(() => {
      if (debouncedSearchRef.current === term) return;
      debouncedSearchRef.current = term;
      setDebouncedSearch(term);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchFilter]);
  // The server's status filter: the Archived list is every archived pour.
  const serverStatus = showArchived ? 'All' : statusFilter;

  // The page shown, for a request that answers after the user moved on.
  const companyId = userIsSuper ? clientFilter : '';
  const listQueryRef = useRef({ page, rowsPerPage, status: serverStatus, search: debouncedSearch, archived: showArchived, companyId });
  listQueryRef.current = { page, rowsPerPage, status: serverStatus, search: debouncedSearch, archived: showArchived, companyId };
  // Only the newest list request may fill the table: a slow answer for an
  // earlier page or filter is dropped.
  const listRequestRef = useRef(0);

  // Load the page shown. Only the first load shows the page spinner, so the
  // map and the search box stay put while later pages and filters load.
  // `quiet` (the poll) keeps the list as it is when the request fails.
  const fetchPours = async (quiet = false) => {
    const query = listQueryRef.current;
    const request = ++listRequestRef.current;
    if (!quiet) setError('');
    try {
      const response = await pourAPI.getPourPage({
        page: query.page,
        pageSize: query.rowsPerPage,
        status: query.status,
        search: query.search,
        archived: query.archived,
        companyId: query.companyId || undefined,
      });
      if (request !== listRequestRef.current) return;
      setPours(response.pours || []);
      setTotalCount(response.total ?? 0);
      setTotalAll(response.total_all ?? 0);
      // The list loaded: an earlier failure's banner goes, quiet or not.
      setError('');
    } catch (err: any) {
      if (request !== listRequestRef.current) return;
      console.error('Error fetching pours:', err);
      if (!quiet) setError(apiErrorText(err, 'Failed to fetch pours data'));
    } finally {
      if (request === listRequestRef.current) setLoading(false);
    }
  };

  // The map pins every project of the filtered list, not only the page's.
  const [mapSummary, setMapSummary] = useState<PourMapSummary | null>(null);
  const [mapSummaryLoaded, setMapSummaryLoaded] = useState(false);
  const mapRequestRef = useRef(0);
  const fetchMapSummary = async () => {
    const { status, search, archived, companyId: clientId } = listQueryRef.current;
    const request = ++mapRequestRef.current;
    try {
      const summary = await pourAPI.getPourMapSummary({
        status,
        search,
        archived,
        companyId: clientId || undefined,
      });
      if (request === mapRequestRef.current) setMapSummary(summary);
    } catch {
      // Non-critical: the map keeps its last pins.
    } finally {
      if (request === mapRequestRef.current) setMapSummaryLoaded(true);
    }
  };

  // A new page, page size, filter or search loads from the server (the
  // status filter and search reset the page themselves).
  useEffect(() => {
    fetchPours();
    // fetchPours reads the query through listQueryRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, serverStatus, debouncedSearch, showArchived, companyId]);
  useEffect(() => {
    fetchMapSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverStatus, debouncedSearch, showArchived, companyId]);

  // A list that shrank (a pour archived, restored or stopped out of the
  // filter, or gone on reload) never leaves the user on an empty page past
  // its end.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (!loading && page > lastPage) setPage(lastPage);
  }, [totalCount, rowsPerPage, page, loading]);

  // The list carries each project's coordinates, client and address: all the
  // map pins need, with no request per project.
  const fetchProjects = async () => {
    try {
      setProjects(await projectsService.listProjects());
    } catch {
      // non-critical — map just won't show project pins
    } finally {
      setProjectsLoaded(true);
    }
  };

  const mappableProjects = useMemo(() => {
    return projects.filter(
      (p) => p.latitude && p.longitude && !isNaN(Number(p.latitude)) && !isNaN(Number(p.longitude))
    );
  }, [projects]);

  // Map pins follow the list's filter and search (every page, not only the
  // one shown): only projects with at least one matching pour are plotted,
  // which keeps the map on projects with active pours instead of every
  // project that happens to have coordinates.
  const projectPins = useMemo(() => {
    const norm = (s?: string | null) => (s ?? '').trim().toLowerCase();
    const listed = new Set((mapSummary?.project_names ?? []).map(norm));
    return mappableProjects
      .filter((proj) => listed.has(norm(proj.project_name)))
      .map((proj) => ({ project: proj }));
  }, [mapSummary, mappableProjects]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
        return 'success';
      case 'in progress':
        return 'warning';
      case 'pending':
      case 'scheduled':
        return 'info';
      case 'stop':
        return 'error';
      default:
        return 'default';
    }
  };

  /** Lifecycle icon paired with the status chip so the state isn't conveyed by
   *  colour alone (sync = running, clock = pending, calendar = scheduled,
   *  tick = complete). */
  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'in progress':
        return <SyncIcon sx={{ fontSize: 16 }} />;
      case 'pending':
        return <ScheduleIcon sx={{ fontSize: 16 }} />;
      case 'scheduled':
        return <EventIcon sx={{ fontSize: 16 }} />;
      default:
        return undefined;
    }
  };

  /** Display label for a pour status — DB stores 'Complete'/'In Progress', UI shows
   *  'Completed'/'In Progress' to match the product terminology. */
  const formatStatusLabel = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
        return t('pours.complete', 'Completed');
      case 'in progress':
        return t('pours.inProgress', 'In Progress');
      case 'pending':
        return t('dashboard.pending', 'Pending');
      case 'archived':
        return t('pours.archived', 'Archived');
      case 'scheduled':
        return t('pours.scheduled', 'Scheduled');
      default:
        return status;
    }
  };

  /** Soft tinted pill colours for the mobile card status chip (matches mock). */
  const getStatusPillSx = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
        return { bgcolor: '#E1F5EE', color: '#0f6e56' };
      case 'in progress':
        return { bgcolor: '#FAEEDA', color: '#9a6a00' };
      case 'pending':
      case 'scheduled':
        return { bgcolor: '#E6F1FB', color: '#185FA5' };
      default:
        return { bgcolor: 'grey.100', color: 'text.secondary' };
    }
  };

  /** "Started 20 Jun" from LoggerStart; a Scheduled pour says when it
   *  starts instead ("Starts 21 Oct 2026, 10:30"). */
  const pourDateRange = (pour: Pour): string | null => {
    if (isScheduledPour(pour)) {
      const starts = formatStartedLabel(pour.StartsAt ?? pour.LoggerStart, pour.TimeZoneIANA);
      return starts ? `${t('pours.starts', 'Starts')} ${starts}` : null;
    }
    if (!pour.LoggerStart) return null;
    const start = formatDateShort(pour.LoggerStart, pour.TimeZoneIANA);
    return start ? `Started ${start}` : null;
  };

  /** Health roll-up line. Renders only once the list API supplies the summary
   *  (WarningCount / AnyStale) — no fabricated data before then. */
  const renderPourHealth = (pour: Pour) => {
    if (pour.PourStatus?.toLowerCase() === 'complete') return null;
    if (pour.WarningCount == null && pour.AnyStale == null) return null;
    const warnings = pour.WarningCount ?? 0;
    // A quiet weather station is said on its own: it is site weather, not
    // one of the loggers.
    const weatherOffline = !!pour.WeatherStationStale;
    const healthy = warnings === 0 && !pour.AnyStale && !weatherOffline;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        {healthy ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#0f6e56', fontSize: '0.78rem', fontWeight: 500 }}>
            <StatusGlyph level="good" size={15} />
            {pour.LoggerCount != null ? ` ${pour.LoggerCount} logger${pour.LoggerCount === 1 ? '' : 's'}` : ' All good'}
          </Box>
        ) : (
          <>
            {warnings > 0 && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#9a6a00', fontSize: '0.78rem', fontWeight: 500 }}>
                <WarningAmberIcon sx={{ fontSize: 15 }} /> {warnings} warning{warnings > 1 ? 's' : ''}
              </Box>
            )}
            {pour.AnyStale && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#9a6a00', fontSize: '0.78rem', fontWeight: 500 }}>
                <CloudOffIcon sx={{ fontSize: 15 }} /> Stale data
              </Box>
            )}
            {weatherOffline && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#9a6a00', fontSize: '0.78rem', fontWeight: 500 }}>
                <CloudOffIcon sx={{ fontSize: 15 }} /> Weather offline
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  /** "Past planned end 29 Sept" under the status of a running pour that has
   *  passed the planned end of its own stop setting (utils/pastPlannedEnd),
   *  so a person sees it and stops it by hand. Display only. */
  const renderPastPlannedEnd = (pour: Pour) => {
    const now = Date.now();
    const past = pastPlannedEnd(
      {
        running: (pour.PourStatus ?? '').toLowerCase() === 'in progress' && !isScheduledPour(pour),
        stopType: pour.StopType,
        xHours: pour.XHours,
        presetStopTime: pour.PreSetStopTime,
        started: pour.Started,
        loggerStart: pour.LoggerStart,
      },
      now,
    );
    if (!past) return null;
    const hint = `${plannedEndSentence(past, now, pour.TimeZoneIANA, t)} ${t(
      'pours.pastPlannedEndRowHint',
      'It is still In Progress. Open the row to stop it when monitoring is finished.',
    )}`;
    return (
      <Tooltip title={hint}>
        <Box
          data-testid="past-planned-end"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: '#9a6a00', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          <WarningAmberIcon sx={{ fontSize: 15 }} />
          {t('pours.pastPlannedEnd', {
            defaultValue: 'Past planned end {{date}}',
            date: formatDateShort(past.end, pour.TimeZoneIANA),
          })}
        </Box>
      </Tooltip>
    );
  };

  /** Compact `dd/MM/yy HH:mm` to keep the Logger Start column tight. */
  const formatLoggerStartTime = (
    utcTimeString: string | null | undefined,
    iana?: string,
  ) => formatInTimeZone(utcTimeString, iana, { year: '2-digit' }).replace(',', '');

  /** "21 Aug 2026, 10:30" in the pour's own timezone for the Started column. */
  const formatStartedLabel = (utcTimeString: string | null | undefined, iana?: string) =>
    formatInTimeZone(utcTimeString, iana, { day: 'numeric', month: 'short', year: 'numeric' });

  /** "648 h" (one decimal under ten hours) for the Age column. */
  const formatAgeLabel = (hours: number | string | null | undefined) => {
    if (hours == null || hours === '') return '-';
    const h = Number(hours);
    if (!Number.isFinite(h)) return '-';
    return `${h < 10 ? h.toFixed(1) : Math.round(h)} h`;
  };

  /** The zone the Started time is shown in, named for that time ("AEST" in
   *  September, "AEDT" in November). Read from the pour's real zone: the
   *  short TimeZone the server sends is made up from the Windows name, and
   *  read as a zone again it can name another place ("AST" is Alaska to the
   *  browser, not Arabia). No zone means the time is shown in UTC. */
  const pourZoneLabel = (pour: Pour) =>
    timeZoneLabel(
      isScheduledPour(pour) ? pour.StartsAt ?? pour.LoggerStart : pour.LoggerStart,
      pour.TimeZoneIANA,
    );

  const applyStatusFilter = (value: string) => {
    const wasArchived = statusFilter === 'Archived';
    const willBeArchived = value === 'Archived';
    // The new filter loads from the server (with page 0) on its own.
    setStatusFilter(value);
    setPage(0);
    if (wasArchived !== willBeArchived) setExpandedPourId(null);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    applyStatusFilter(event.target.value);
  };

  /** Cache a row's quick view with the time it was fetched. The open row
   *  re-renders (the ref cache alone would not redraw its panel) and drops
   *  any load error, since its data has now arrived. */
  const storeDashboard = useCallback((pourId: string, data: DashboardResponse) => {
    dashboardCache.current[pourId] = { data, fetchedAt: Date.now() };
    if (expandedPourIdRef.current === pourId) {
      setExpandedError('');
      setDashboardVersion((v) => v + 1);
    }
  }, []);

  /** Load a row's quick view in the background (hover, or an old cached
   *  entry when the row is opened). Nothing happens while the cached entry
   *  is younger than the poll interval or a load is already running; a
   *  failure keeps whatever is cached. */
  const fetchDashboardData = useCallback((pourId: string) => {
    if (isFreshEntry(dashboardCache.current[pourId]) || prefetchingRef.current.has(pourId)) return;
    prefetchingRef.current.add(pourId);

    if (!limitsCache.current[pourId]) {
      pourAPI.getPourLimits(pourId)
        .then((limits) => {
          limitsCache.current[pourId] = limits;
          setLimitsVersion((v) => v + 1);
        })
        .catch(() => {});
    }

    pourAPI.getDashboardData(pourId)
      .then((data) => storeDashboard(pourId, data))
      .catch(() => {})
      .finally(() => {
        prefetchingRef.current.delete(pourId);
      });
  }, [storeDashboard]);

  const handleRowClick = (pour: Pour) => {
    if (!pour.PourID) return;

    if (expandedPourId === pour.PourID) {
      setExpandedPourId(null);
      setExpandedError('');
      return;
    }

    setExpandedPourId(pour.PourID);
    setExpandedError('');

    if (!limitsCache.current[pour.PourID]) {
      pourAPI.getPourLimits(pour.PourID)
        .then((limits) => {
          limitsCache.current[pour.PourID] = limits;
          setLimitsVersion((v) => v + 1);
        })
        .catch(() => {});
    }

    // A cached quick view shows straight away. One older than the poll
    // interval (hovered or opened long ago) would show an old last read and
    // maturity, so it reloads quietly and is replaced when the new data
    // lands; a fresh one is left alone.
    if (dashboardCache.current[pour.PourID]) {
      setExpandedLoading(false);
      fetchDashboardData(pour.PourID);
      return;
    }

    // The first load of an uncached row. Only a row that is still open shows
    // the error or ends the spinner: a slow load that lands after another
    // row was opened just fills the cache.
    const pourId = pour.PourID;
    const stillOpen = () => expandedPourIdRef.current === pourId;
    setExpandedLoading(true);
    pourAPI.getDashboardData(pourId)
      .then((data) => storeDashboard(pourId, data))
      .catch((err: any) => {
        if (stillOpen()) setExpandedError(apiErrorText(err, 'Failed to load data'));
      })
      .finally(() => {
        if (stillOpen()) setExpandedLoading(false);
      });
  };

  // ---- Refresh: the list and the open row's quick view ----

  /** Reload a row's quick view (dashboard and limits) in place: no spinner,
   *  the panel shows its last data until the new data lands, and keeps it
   *  when the request fails. */
  const refreshExpandedRow = useCallback((pourId: string) => {
    pourAPI.getDashboardData(pourId)
      .then((data) => storeDashboard(pourId, data))
      .catch(() => {});
    pourAPI.getPourLimits(pourId)
      .then((limits) => {
        limitsCache.current[pourId] = limits;
        setLimitsVersion((v) => v + 1);
      })
      .catch(() => {});
  }, [storeDashboard]);

  /** Forget every cached quick view except the open row's: the others
   *  reload with current readings when next hovered or opened. */
  const keepOnlyOpenRow = (open: string | null) => {
    const openData = open ? dashboardCache.current[open] : undefined;
    const openLimits = open ? limitsCache.current[open] : undefined;
    dashboardCache.current = open && openData ? { [open]: openData } : {};
    limitsCache.current = open && openLimits ? { [open]: openLimits } : {};
  };

  /** The Refresh button: forget the cached quick views, reload the list,
   *  and reload the open row in place. */
  const handleRefresh = () => {
    const open = showArchived ? null : expandedPourId;
    keepOnlyOpenRow(open);
    fetchPours();
    fetchMapSummary();
    if (open) refreshExpandedRow(open);
  };

  // Poll every 5 minutes while the page is visible: the list (quietly, see
  // fetchPours) and the open row's quick view, dropping every other cached
  // quick view so no row opens on old readings. A tab that comes back after
  // a longer absence refreshes straight away. The poll reads the current
  // filter and open row through a ref, so the timer is set up once.
  const lastPollRef = useRef(Date.now());
  const pollRef = useRef<() => void>(() => {});
  pollRef.current = () => {
    lastPollRef.current = Date.now();
    const open = showArchived ? null : expandedPourId;
    keepOnlyOpenRow(open);
    fetchPours(true);
    fetchMapSummary();
    if (open) refreshExpandedRow(open);
  };
  useEffect(() => {
    const visible = () => document.visibilityState !== 'hidden';
    const timer = window.setInterval(() => {
      if (visible()) pollRef.current();
    }, POLL_MS);
    const onVisibilityChange = () => {
      if (visible() && Date.now() - lastPollRef.current >= POLL_MS) pollRef.current();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // ---- Stop pour ----
  const handleStopConfirm = async () => {
    const target = stopTargetPour;
    if (!target?.PourID) return;
    setStoppingPourId(target.PourID);
    setStopError('');
    setStopSuccess('');
    try {
      const result = await pourAPI.stopPour(target.PourID);
      setStopTargetPour(null);
      setStopTankCount(null);
      // Optimistic update: flip the row's status locally so the Stop icon
      // disappears immediately, then refresh the list to pull canonical
      // server state (including LoggerEnd, etc.).
      const newStatus = result.already_stopped ? target.PourStatus : 'Complete';
      setPours((prev) =>
        prev.map((p) =>
          p.PourID === target.PourID
            // Stopping also locks the pour server-side
            ? { ...p, PourStatus: newStatus, Locked: result.already_stopped ? p.Locked : true }
            : p,
        ),
      );
      // The open row keeps its quick view while it reloads in place, so it
      // never goes blank: the new data (final values, logging ended)
      // replaces it when it lands, and a failed reload keeps the old view.
      // The cached view is marked old first, so a later hover or open tries
      // again if this reload fails.
      const cached = dashboardCache.current[target.PourID];
      if (cached) dashboardCache.current[target.PourID] = { ...cached, fetchedAt: 0 };
      // Under a status filter it no longer matches (In progress, the
      // default), the stopped pour leaves the table: its row closes rather
      // than reload a panel nobody can see on every poll and Refresh.
      const stillShown =
        statusFilter === 'All' || (newStatus ?? '').toLowerCase() === statusFilter.toLowerCase();
      if (expandedPourIdRef.current === target.PourID) {
        if (stillShown) {
          refreshExpandedRow(target.PourID);
        } else {
          setExpandedPourId(null);
          setExpandedError('');
        }
      }
      setStopSuccess(
        result.already_stopped
          ? t('pours.alreadyStopped', 'Pour was already stopped')
          : result.tanks_notified
          ? t('pours.stopSuccessWithTanks', {
              defaultValue: 'Pour stopped. {{count}} tank(s) reset.',
              count: result.tanks_notified,
            })
          : t('pours.stopSuccess', 'Pour stopped.'),
      );
      // Best-effort quiet refresh of the page and the map (the table does
      // not flash); if it fails the optimistic update still stands.
      fetchPours(true);
      fetchMapSummary();
    } catch (err: any) {
      setStopError(apiErrorText(err, 'Failed to stop pour'));
    } finally {
      setStoppingPourId(null);
    }
  };

  // ---- Lock / unlock ----
  const handleToggleLock = async (pour: Pour) => {
    const locking = !pour.Locked;
    setLockBusyPourId(pour.PourID);
    setStopError('');
    try {
      const result = locking
        ? await pourAPI.lockPour(pour.PourID)
        : await pourAPI.unlockPour(pour.PourID);
      // In place: the open row keeps its cached quick view, only its Lock /
      // Unlock action changes.
      setPours((prev) =>
        prev.map((p) => (p.PourID === pour.PourID ? { ...p, Locked: result.Locked } : p)),
      );
      setStopSuccess(
        result.Locked
          ? t('pours.lockSuccess', 'Pour locked. Changes are now blocked.')
          : t('pours.unlockSuccess', 'Pour unlocked. Changes can now be made.'),
      );
    } catch (err: any) {
      setStopError(apiErrorText(err, 'Failed to update lock'));
    } finally {
      setLockBusyPourId(null);
    }
  };

  /** Take a pour that left the list (archived, deleted or restored) out of
   *  the page: its row closes if it was open and its cached quick view is
   *  dropped, so no blank panel stays behind and no poll or Refresh reloads
   *  a pour that is no longer listed. */
  const removePourFromList = (pourId: string) => {
    setPours((prev) => prev.filter((p) => p.PourID !== pourId));
    if (expandedPourIdRef.current === pourId) {
      setExpandedPourId(null);
      setExpandedError('');
    }
    delete dashboardCache.current[pourId];
    delete limitsCache.current[pourId];
    // The server refills the page (the next pour moves up) and the counts.
    fetchPours(true);
    fetchMapSummary();
  };

  // ---- Archive (soft delete) ----
  const handleArchiveConfirm = async () => {
    const target = archiveTargetPour;
    if (!target?.PourID) return;
    setArchivingPour(true);
    setStopError('');
    try {
      const result = await pourAPI.archivePour(target.PourID);
      setArchiveTargetPour(null);
      removePourFromList(target.PourID);
      setStopSuccess(
        result?.action === 'deleted'
          ? t('pours.deleteSuccess', 'Pour deleted.')
          : t('pours.archiveSuccess', 'Pour archived.'),
      );
    } catch (err: any) {
      setStopError(apiErrorText(err, 'Failed to archive pour'));
      setArchiveTargetPour(null);
    } finally {
      setArchivingPour(false);
    }
  };

  // ---- Restore: bring an archived pour back into the live lists ----
  const handleRestore = async (pour: Pour) => {
    if (!pour.PourID || restoringPourId) return;
    setRestoringPourId(pour.PourID);
    setStopError('');
    try {
      await pourAPI.restorePour(pour.PourID);
      // It leaves the Archived list (it is live again).
      removePourFromList(pour.PourID);
      setStopSuccess(t('pours.restoreSuccess', 'Pour restored. It is back in the live list.'));
    } catch (err: any) {
      setStopError(apiErrorText(err, 'Failed to restore pour'));
    } finally {
      setRestoringPourId(null);
    }
  };

  // ---- Duplicate: prefill the create wizard from this pour ----
  const handleDuplicate = (pour: Pour) => {
    navigate('/pours/new', { state: { duplicateFromPourId: pour.PourID } });
  };

  const handleRowHover = useCallback((pourId: string) => {
    fetchDashboardData(pourId);
  }, [fetchDashboardData]);

  // Render expanded detail content for a pour row
  const renderExpandedContent = (pourId: string, pour?: Pour) => {
    const cachedData = dashboardCache.current[pourId]?.data;
    const pourLimits = limitsCache.current[pourId] ?? [];
    const tempMarkers = tempMarkersFromLimits(pourLimits);
    const barScaleMax = scaleMaxFromLimits(pourLimits);

    // Archived pours have no live detail/dashboard; the only action is Restore.
    if (pour?.Archived) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
          {canManagePours && (
            <Button
              variant="outlined"
              size="small"
              startIcon={restoringPourId === pour.PourID ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
              disabled={restoringPourId === pour.PourID}
              onClick={(e) => { e.stopPropagation(); handleRestore(pour); }}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {t('pours.restore', 'Restore pour')}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('pours.archivedNote', 'Archived pours are hidden from lists and dashboards. Restoring returns the pour exactly as it was.')}
          </Typography>
        </Box>
      );
    }

    if (expandedLoading && expandedPourId === pourId) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={22} />
        </Box>
      );
    }

    if (expandedError && expandedPourId === pourId) {
      return <Alert severity="error" variant="outlined" sx={{ py: 0 }}>{expandedError}</Alert>;
    }

    if (!cachedData) return null;

    // Action buttons, in design order: Setting / Dashboard / Stop Pour /
    // Generate Report / Duplicate / Lock or Unlock / X (delete or archive).
    // Shared markup for the quick-view panel (doc 27.07).
    // Built before the empty-locations return so a pour with no monitoring
    // locations can still be opened, stopped, or edited to add loggers.
    // Lifecycle model: Pending = edit or delete; In Progress = edit or stop;
    // Completed = lock toggle, archive, duplicate. Stop is the only action
    // that ends monitoring; delete/archive only change visibility.
    const isPending = pour?.PourStatus === 'Pending';
    const isInProgress = pour?.PourStatus === 'In Progress';
    const isActive = isInProgress || isPending;
    const isCompleted = pour ? !isActive : false;
    // Monitoring locations; a weather station is the pour's site weather,
    // never one of them. A pour with either has something to show.
    const hasLocations = cachedData.locations.length > 0;
    const siteWeather = linkedSiteWeather(cachedData.site_weather);
    const hasContent = hasLocations || siteWeather !== null;
    const siteWeatherLine = siteWeather ? (
      <SiteWeatherLine
        site={siteWeather}
        pourStatus={cachedData.pour?.status}
        windSpeedUnit={cachedData.pour?.wind_speed_unit}
        timezoneIana={cachedData.pour?.timezone_iana}
      />
    ) : null;

    // One ordered list drives both renderers so the slots never move between
    // statuses: Setting, Dashboard, Stop Pour, Generate Report, Duplicate,
    // Lock / Unlock, then the destructive X on its own at the end.
    type PourAction = {
      key: string;
      label: string;
      icon: React.ReactNode;
      show: boolean;
      disabled?: boolean;
      destructive?: boolean;
      onClick: () => void;
    };
    const actions: PourAction[] = pour ? [
      {
        key: 'setting',
        label: t('pours.setting', 'Setting'),
        icon: <SettingIcon sx={{ fontSize: 20 }} />,
        show: true,
        onClick: () => navigate(`/pours/${pour.PourID}`),
      },
      {
        key: 'dashboard',
        label: t('pours.dashboard', 'Dashboard'),
        icon: <DashboardIcon sx={{ fontSize: 20 }} />,
        show: hasContent,
        onClick: () => navigate(`/pours/${pour.PourID}/dashboard`),
      },
      {
        key: 'stop',
        label: t('pours.stopPour', 'Stop Pour'),
        icon: <StopCircleIcon sx={{ fontSize: 20 }} />,
        show: isInProgress,
        disabled: stoppingPourId === pour.PourID,
        onClick: () => openStopDialog(pour),
      },
      {
        key: 'report',
        label: t('pours.report', 'Generate Report'),
        icon: <ReportIcon sx={{ fontSize: 20 }} />,
        show: hasContent,
        onClick: () => setReportDialogPour(pour),
      },
      {
        key: 'duplicate',
        label: t('pours.duplicate', 'Duplicate'),
        icon: <DuplicateIcon sx={{ fontSize: 20 }} />,
        show: canManagePours && hasContent,
        onClick: () => handleDuplicate(pour),
      },
      {
        key: 'lock',
        label: pour.Locked ? t('pours.unlock', 'Unlock') : t('pours.lock', 'Lock'),
        icon: pour.Locked ? <LockIcon sx={{ fontSize: 20 }} /> : <LockOpenIcon sx={{ fontSize: 20 }} />,
        show: canManagePours && isCompleted,
        disabled: lockBusyPourId === pour.PourID,
        onClick: () => handleToggleLock(pour),
      },
      {
        key: 'remove',
        label: isPending ? t('pours.deletePour', 'Delete Pour') : t('pours.archivePour', 'Archive Pour'),
        icon: <RemoveIcon sx={{ fontSize: 20 }} />,
        show: canManagePours && (isPending || isCompleted),
        destructive: true,
        onClick: () => setArchiveTargetPour(pour),
      },
    ].filter((a) => a.show) : [];

    const primaryActions = actions.filter((a) => !a.destructive);
    const destructiveAction = actions.find((a) => a.destructive);

    // Labelled renderer (demo): the same 32px segmented strip as the original
    // icon row, with a short text label beside each icon. The destructive
    // action stands alone after a gap and only turns red on hover.
    const labelledToggleSx = {
      textTransform: 'none',
      px: 1.25,
      gap: 0.5,
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      color: 'text.secondary',
      borderColor: 'grey.300',
      '& svg': { fontSize: 18 },
      '&:hover': { backgroundColor: 'grey.100', color: 'text.primary' },
    } as const;
    const destructiveToggleSx = {
      ...labelledToggleSx,
      '&:hover': { color: 'error.main', borderColor: 'error.main', backgroundColor: 'rgba(211, 47, 47, 0.06)' },
    } as const;
    const renderLabelled = (a: PourAction) => (
      <ToggleButton
        key={a.key}
        value={a.key}
        disabled={a.disabled}
        onClick={(e) => { e.stopPropagation(); a.onClick(); }}
        sx={a.destructive ? destructiveToggleSx : labelledToggleSx}
      >
        {a.icon}
        {a.label}
      </ToggleButton>
    );

    // Compact renderer (original): icon-only toggle strip with tooltips.
    const renderCompact = (a: PourAction) => (
      <ToggleButton key={a.key} value={a.key} disabled={a.disabled} onClick={(e) => { e.stopPropagation(); a.onClick(); }}>
        <Tooltip title={a.label}>{a.icon as React.ReactElement}</Tooltip>
      </ToggleButton>
    );

    const actionButtons = pour ? (
      LABELLED_ACTION_ROW ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" sx={{ height: 32 }}>
            {primaryActions.map(renderLabelled)}
          </ToggleButtonGroup>
          {destructiveAction && (
            <ToggleButtonGroup size="small" sx={{ height: 32 }}>
              {renderLabelled(destructiveAction)}
            </ToggleButtonGroup>
          )}
        </Box>
      ) : (
        <ToggleButtonGroup size="small" sx={{ height: 32, '& .MuiToggleButton-root': { px: 1, borderColor: 'grey.300', color: 'text.secondary', '&:hover': { backgroundColor: 'grey.100' } } }}>
          {actions.map(renderCompact)}
        </ToggleButtonGroup>
      )
    ) : null;

    if (!hasLocations) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
          {actionButtons}
          {/* A pour that records only site weather: its line, no rows. */}
          {siteWeatherLine}
          {!siteWeatherLine && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {isPending
                ? t('pours.pendingNoLocations', 'This pour is waiting for a logger. Add a monitoring location to start recording.')
                : t('pours.noLocations', 'This pour has no monitoring locations.')}
            </Typography>
            {canManagePours && pour && !pour.Locked && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => { e.stopPropagation(); navigate(`/pours/${pour.PourID}/edit`); }}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {t('pours.addLocation', 'Add monitoring location')}
              </Button>
            )}
          </Box>
          )}
        </Box>
      );
    }

    // Reference max for temperature profile — highest max_temp across all
    // TEMPERATURE locations (humidity channels carry %RH, not °C, and would
    // skew the shared temperature scale).
    const referenceMax = Math.max(
      ...cachedData.locations
        .filter((l) => !isHumidityLocation(l))
        .map((l) => l.max_temp ?? 0),
      1,
    );

    // Targets only count on locations that accrue maturity (not humidity,
    // site hardware or Temperature Only locations).
    const hasTargets = cachedData.locations.some(
      (l) => locationHasMaturity(l) && l.targets && l.targets.length > 0,
    );
    // Maturity naming follows the pour's method (only "Nurse Saul" is TTF);
    // strength carries the company unit. Without a usable calibration curve
    // there is no strength, so the value column shows maturity instead.
    const maturity = maturityMeta(cachedData.pour);
    const strengthUnit = resolveStrengthUnit(cachedData.pour?.strength_unit);
    const maturityOnly = pourHasCurve(cachedData.pour) === false;
    const calibratedRange = cachedData.pour?.calibrated_range;
    // Multi-function pours: rows grouped by monitoring function, every group
    // introduced by a quiet label (single-function pours get none).
    const { ordered: orderedLocations, groupLabels } = orderByFunction(cachedData.locations);
    // The 0..scaleMax axis belongs under the last row that draws a temperature
    // bar — humidity rows render a %RH bar and can't host it.
    const lastTempBarIdx = orderedLocations.reduce(
      (last, l, i) => (isHumidityLocation(l) ? last : i),
      -1,
    );
    // Ambient bars skip the limit markers but keep the shared 0..scaleMax
    // axis (forceScale) whenever the pour actually has limits, so equal
    // temperatures line up across concrete and ambient rows.
    const hasLimitMarkers = tempMarkers.length > 0;
    // The mode toggle is shared across rows, so a temp-only pour expanded
    // after picking "strength" elsewhere must fall back to temperature.
    const viewMode = hasTargets ? expandedViewMode : 'temperature';
    const statusTooltip = (s?: string) =>
      s === 'good' ? 'UP' : s === 'warning' ? 'Sensor Error' : s === 'error' ? 'DOWN' : 'N/A';
    // A location whose logging ended (the pour is completed, or its own end
    // has passed) shows a grey "Logging ended" mark in both layouts, never a
    // red "DOWN" for a logger that has simply been taken away.
    const loggingEnded = (l: (typeof cachedData.locations)[number]) =>
      isCompleted || isLoggingEnded(l);
    // Likewise a location waiting for its start (no reading yet, start ahead)
    // shows a blue "Scheduled" mark, not its idle logger's red "DOWN".
    const waitingToStart = (l: (typeof cachedData.locations)[number]) =>
      !loggingEnded(l) && locationOnlineStatus(l).level === 'scheduled';
    const markTooltip = (l: (typeof cachedData.locations)[number]) =>
      loggingEnded(l)
        ? t('pours.loggingEnded', 'Logging ended')
        : waitingToStart(l)
          ? t('pours.scheduled', 'Scheduled')
          : statusTooltip(l.status);
    const scheduledMark = <EventIcon data-testid="location-scheduled" sx={{ fontSize: 18, color: '#185FA5' }} />;

    // Temperature / strength mode toggle — shared markup.
    const modeToggle = hasTargets ? (
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_e, v) => v && setExpandedViewMode(v)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.72rem', lineHeight: 1.4 },
          // Filled selected state so the active profile is obvious at a glance.
          '& .MuiToggleButton-root.Mui-selected': {
            bgcolor: 'grey.800', color: 'common.white', borderColor: 'grey.800',
            '&:hover': { bgcolor: 'grey.900' },
          },
        }}
      >
        <ToggleButton value="temperature">Temperature Profile</ToggleButton>
        <ToggleButton value="strength">Target Progress</ToggleButton>
      </ToggleButtonGroup>
    ) : (
      <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
        Temperature Profile
      </Typography>
    );

    // Group headings ("Concrete Maturity", "Ambient Temp & Humidity"): a step
    // darker and larger than the original text.disabled caption.
    const groupHeadingSx = PANEL_CLARITY
      ? { fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }
      : { fontSize: '0.65rem', fontWeight: 600, color: 'text.disabled', letterSpacing: 0.3 };

    type LocationRow = (typeof cachedData.locations)[number];
    // The toggle only applies to maturity rows. Site-conditions channels
    // (ambient Temp & Humidity, weather) and Temperature Only locations have
    // no maturity or strength, so their bar and value stay in temperature
    // mode in either view.
    const rowViewMode = (l: LocationRow) =>
      locationHasMaturity(l) ? viewMode : 'temperature';
    // Why a temperature bar is empty, in words, so a blank track is not read
    // as "zero degrees".
    const emptyBarLabel = (l: LocationRow): string | null => {
      if (!PANEL_CLARITY) return null;
      if (rowViewMode(l) !== 'temperature' || isHumidityLocation(l)) return null;
      if (l.current_temp != null) return null;
      return l.logger_code
        ? t('pours.noReadingYet', 'No reading yet')
        : t('pours.noLoggerAssigned', 'No logger assigned');
    };
    const renderBar = (location: LocationRow, idx: number) => {
      const label = emptyBarLabel(location);
      const bar = renderBarInner(location, idx);
      if (!label) return bar;
      return (
        <Box sx={{ position: 'relative' }}>
          {bar}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 22,
              display: 'flex', alignItems: 'center', pl: 1.5,
              fontSize: '0.7rem', color: 'text.disabled', pointerEvents: 'none',
            }}
          >
            {label}
          </Typography>
        </Box>
      );
    };
    const renderBarInner = (location: LocationRow, idx: number) =>
      rowViewMode(location) === 'temperature' ? (
        isHumidityLocation(location) ? (
          <HumidityBar value={location.current_temp} height={22} />
        ) : (
        <TemperatureProfileBar
          maxMarker
          flat={isSiteConditionsChannel(location)}
          currentTemp={location.current_temp}
          maxTemp={location.max_temp}
          referenceMax={referenceMax}
          // Site-conditions channels (ambient T&H, weather) have no concrete
          // limits — hide the markers but keep the shared axis scale.
          limits={isSiteConditionsChannel(location) ? undefined : tempMarkers}
          forceScale={isSiteConditionsChannel(location) && hasLimitMarkers}
          scaleMax={barScaleMax}
          showScale={idx === lastTempBarIdx}
          sx={{ height: 22 }}
        />
        )
      ) : (
        <MaturityProgress
          targets={location.targets}
          strengthUnit={strengthUnit}
          maturity={maturity}
          sx={{ height: 22 }}
        />
      );
    // Strength mode value: the calibrated-range rule ("24.3", "< 3.3",
    // "> 41.0" with a tooltip, "NCS*"), then the maturity index. Desktop
    // rows leave the unit to the column heading; mobile rows carry it.
    // Without a usable curve the maturity alone is shown: on mobile (no
    // column heading) with its name and specified temperature ("EA at
    // 20 °C: 75.6 hrs"), on desktop under the "EA at 20 °C (hrs)" heading.
    // 1 dp half up, as the backend progress label.
    const renderStrengthValue = (location: LocationRow, withUnit: boolean) => {
      if (maturityOnly) {
        return (
          <Typography variant="body2" fontWeight={600} fontSize="0.85rem" noWrap>
            {withUnit
              ? formatMaturityWithIndex(location.current_ea, maturity)
              : location.current_ea != null ? formatHalfUp(location.current_ea) : '-'}
          </Typography>
        );
      }
      const reading = resolveStrengthReading(location.strength, location.current_strength);
      const tip = strengthReadingTooltip(reading);
      return (
        <Typography variant="body2" fontWeight={600} fontSize="0.85rem" noWrap>
          <StrengthTip title={tip}>
            <span style={tipUnderline(tip)}>
              {formatStrengthReading(reading, withUnit ? strengthUnit : null)}
            </span>
          </StrengthTip>
          {location.current_ea != null && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              fontSize="0.65rem"
              sx={{ ml: 0.5 }}
              title={`${maturity.index_label}: ${formatMaturity(location.current_ea, maturity)}`}
            >
              ({formatMaturityWithIndex(location.current_ea, maturity)})
            </Typography>
          )}
          <OutsideRangeTargets
            variant="icon"
            targets={location.targets}
            range={calibratedRange}
            strengthUnit={strengthUnit}
          />
        </Typography>
      );
    };
    const renderValue = (location: LocationRow) =>
      rowViewMode(location) === 'temperature' ? (
        <Typography variant="body2" fontWeight={600} fontSize="0.85rem" noWrap>
          {location.current_temp != null
            ? `${location.current_temp.toFixed(1)} ${isHumidityLocation(location) ? '%' : '°C'}`
            : '-'}
        </Typography>
      ) : (
        renderStrengthValue(location, true)
      );

    // Legend for the marks on the temperature bars, in words. Built from the
    // pour's own limits so the colours and values always match what is drawn.
    // Left-aligned with the action strip; limits listed coolest to hottest.
    const renderLegend = (paddingLeft: number) =>
      PANEL_CLARITY && viewMode === 'temperature' && hasLimitMarkers ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 0.75, pl: `${paddingLeft}px` }}>
          {[...tempMarkers].sort((a, b) => a.temp - b.temp).map((m) => (
            <Box key={`${m.label}-${m.temp}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 3, height: 12, borderRadius: 0.5, bgcolor: m.color || 'error.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {m.label || t('pours.limit', 'Limit')} {m.temp} °C
              </Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '7px solid', borderTopColor: 'grey.700' }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {t('pours.peakMarker', 'Highest reading so far')}
            </Typography>
          </Box>
        </Box>
      ) : null;

    // Top-right switch between the detailed and compact panel layouts.
    const viewSwitch = (
      <Tooltip title={detailedView ? t('pours.compactView', 'Switch to compact view') : t('pours.detailedView', 'Switch to detailed view')}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); toggleDetailedView(); }}
          aria-label={detailedView ? t('pours.compactView', 'Switch to compact view') : t('pours.detailedView', 'Switch to detailed view')}
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          {detailedView ? <CompactViewIcon sx={{ fontSize: 18 }} /> : <DetailedViewIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>
    );

    // ---- Mobile: stacked per-location rows with full-width bars ----
    if (isMobile) {
      return (
        <Box sx={{ py: 0.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
            {actionButtons}
            {modeToggle}
            <Box sx={{ ml: 'auto' }}>{viewSwitch}</Box>
          </Box>
          {siteWeatherLine}
          {renderLegend(0)}
          {orderedLocations.map((location, idx) => (
            <React.Fragment key={location.id}>
            {groupLabels.has(idx) && (
              <Typography
                variant="caption"
                sx={{ display: 'block', ...groupHeadingSx, pt: idx === 0 ? 0 : 0.75, pb: 0.25 }}
              >
                {groupLabels.get(idx)}
              </Typography>
            )}
            <Box
              onClick={pour ? (e) => {
                e.stopPropagation();
                navigate(`/pours/${pour.PourID}/dashboard`, {
                  state: { locationId: location.id, view: 'location' },
                });
              } : undefined}
              sx={{
                py: PANEL_CLARITY ? 1.25 : 1,
                borderTop: '1px solid',
                borderColor: 'grey.100',
                ...(pour && { cursor: 'pointer', '&:active': { backgroundColor: 'grey.50' } }),
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography variant="body2" fontWeight={600} fontSize="0.82rem" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {location.name || location.id.slice(-8)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {renderValue(location)}
                  <Tooltip title={markTooltip(location)}>
                    <Box sx={{ display: 'flex' }}>
                      {loggingEnded(location)
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                        : waitingToStart(location)
                          ? scheduledMark
                          : <StatusGlyph level={loggerLevel(location.status)} size={18} />}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
              {renderBar(location, idx)}
            </Box>
            </React.Fragment>
          ))}
        </Box>
      );
    }

    // Fixed widths for columns — shared by header AND rows (desktop).
    // Wide enough for the full admin action set (setting/dashboard/stop|
    // report/duplicate/lock/archive) without clipping.
    const NAME_W = 250;
    // Strength rows also carry the maturity with its name and unit
    // ("24.3 (EA at 20 °C: 120 hrs)"), so that column is wider.
    const VAL_W = viewMode === 'strength' && !maturityOnly ? 180 : 110;
    const STATUS_W = 90;

    // "Latest" while the pour is live, "Final" once completed, in both layouts.
    const valueHeading = viewMode === 'temperature'
      ? (isCompleted ? t('pours.finalValue', 'Final') : t('pours.latestValue', 'Latest'))
      : maturityOnly
        ? maturityShortTitle(maturity)
        : `Strength (${strengthUnit})`;
    const columnHeadings = (
      <>
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ width: VAL_W, textAlign: 'center', flexShrink: 0 }}
        >
          {valueHeading}
        </Typography>
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ width: STATUS_W, textAlign: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('pours.statusColumn', 'Status')}
        </Typography>
      </>
    );


    return (
      <Box sx={{ px: 1, py: 0.5 }}>
        {/* ---- Header row — same flex structure as data rows ---- */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
          {LABELLED_ACTION_ROW ? (
            /* Labelled strip is wider than the name column, so it takes its
               natural width and the profile toggle follows it. */
            <Box sx={{ display: 'flex', flexShrink: 0, pr: 2 }}>
              {actionButtons}
            </Box>
          ) : (
            /* Compact action chips — fixed width matching location name column */
            <Box sx={{ display: 'flex', gap: 0.5, width: NAME_W, flexShrink: 0, pr: 1, overflow: 'hidden' }}>
              {actionButtons}
            </Box>
          )}
          {/* Same flex:1 as bar col — holds the profile toggle on the left and
              the detailed / compact switch on the right, so the fixed value
              and status columns after it never shift. */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            {modeToggle}
            {viewSwitch}
          </Box>

        </Box>
        {siteWeatherLine}
        {renderLegend(0)}

        {/* ---- Location rows ---- */}
        {orderedLocations.map((location, idx) => (
          <React.Fragment key={location.id}>
          {/* Quiet group label above each monitoring-function group
              (multi-function pours only). */}
          {idx === 0 ? (
            /* First line above the bars (both layouts): group heading on the
               left, value and status headings over their columns on the right. */
            <Box sx={{ display: 'flex', alignItems: 'flex-end', pt: 0.25, pb: 0.25 }}>
              <Typography variant="caption" sx={{ flex: 1, minWidth: 0, ...groupHeadingSx }}>
                {groupLabels.get(0) ?? ''}
              </Typography>
              {columnHeadings}
            </Box>
          ) : groupLabels.has(idx) && (
            <Typography
              variant="caption"
              sx={{ display: 'block', ...groupHeadingSx, pt: idx === 0 ? 0.25 : 1, pb: 0.25 }}
            >
              {groupLabels.get(idx)}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              '&:hover': { backgroundColor: 'grey.50' },
              borderTop: '1px solid',
              borderColor: 'grey.100',
            }}
          >
            {/* Location name */}
            <Typography
              variant="body2"
              fontWeight={600}
              fontSize="0.82rem"
              noWrap
              sx={{ width: NAME_W, flexShrink: 0, pr: 1 }}
            >
              {location.name || location.id.slice(-8)}
            </Typography>

            {/* Bar */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {renderBar(location, idx)}
            </Box>

            {/* Value */}
            <Box sx={{ width: VAL_W, textAlign: 'center', flexShrink: 0 }}>
              {rowViewMode(location) === 'temperature' ? (
                <Typography variant="body2" fontWeight={600} fontSize="0.85rem" noWrap>
                  {location.current_temp != null
                    ? `${location.current_temp.toFixed(1)} ${isHumidityLocation(location) ? '%' : '°C'}`
                    : '-'}
                </Typography>
              ) : (
                renderStrengthValue(location, false)
              )}
            </Box>

            {/* Status glyph (shape + colour) with tooltip */}
            <Tooltip title={markTooltip(location)}>
              <Box sx={{ width: STATUS_W, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                {loggingEnded(location)
                  ? <CheckCircleIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                  : waitingToStart(location)
                    ? scheduledMark
                    : <StatusGlyph level={loggerLevel(location.status)} size={18} sx={{ cursor: 'pointer' }} />}
              </Box>
            </Tooltip>



          </Box>
          </React.Fragment>
        ))}
      </Box>
    );
  };

  // Default map center (Sydney, Australia)
  const defaultCenter: [number, number] = [-33.8688, 151.2093];
  // Zoom in on the project of the newest pour in the filtered list (the
  // server names it), rather than zooming out to fit every marker. Pours
  // carry no coordinates; they live on the project. Falls back: first
  // pinned project, then Sydney.
  const latestPourProject = mapSummary?.latest_project_name
    ? mappableProjects.find(p => p.project_name === mapSummary.latest_project_name)
    : undefined;
  const focusPosition: [number, number] | null =
    latestPourProject
      ? [Number(latestPourProject.latitude), Number(latestPourProject.longitude)]
      : projectPins.length > 0
        ? [Number(projectPins[0].project.latitude), Number(projectPins[0].project.longitude)]
        : null;
  const mapCenter: [number, number] = focusPosition ?? defaultCenter;
  // Hold the map mount until the list, the projects and the map summary have
  // loaded, so it initialises already centred on the focus target rather
  // than panning from the fallback.
  const mapDataReady = !loading && projectsLoaded && mapSummaryLoaded;

  // When every listed pour shares one timezone, name it once in the Started
  // heading instead of repeating it on every row. Read from the page shown,
  // once per change of it: labelling every pour on every render (a row
  // click) froze the page for seconds when the browser held the whole list.
  const listTimeZone = useMemo(() => {
    const zones = new Set(pours.map(pourZoneLabel));
    return zones.size === 1 ? zones.values().next().value ?? null : null;
    // pourZoneLabel reads only the pour it is given.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pours]);
  const showTimeZoneColumn = !LIST_CLARITY || !listTimeZone;
  const totalColumns = isMobile ? 8 : showTimeZoneColumn ? 10 : 9;

  if (loading && pours.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters + actions */}
      {isMobile ? (
        <Box mb={2} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Status chips, search and refresh share one row to save vertical space.
              Search stays collapsed to an icon until tapped, so the chips keep their
              full width and aren't squeezed. (New Pour lives in the bottom nav.) */}
          {searchOpen ? (
            <>
              <TextField
                size="small"
                autoFocus
                sx={{ flex: 1, minWidth: 0 }}
                variant="outlined"
                placeholder={t('pours.searchShort', 'Search pours')}
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={t('common.close', 'Close')}>
                <IconButton
                  size="small"
                  onClick={() => { setSearchOpen(false); setSearchFilter(''); }}
                  aria-label={t('common.close', 'Close')}
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', overflowX: 'auto', flex: 1, minWidth: 0, '&::-webkit-scrollbar': { display: 'none' } }}>
                {[
                  { value: 'All', label: t('common.all') },
                  { value: 'In progress', label: t('pours.inProgress') },
                  { value: 'Pending', label: t('dashboard.pending') },
                  { value: 'Complete', label: t('pours.complete') },
                  ...(canManagePours ? [{ value: 'Archived', label: t('pours.archived', 'Archived') }] : []),
                ].map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    onClick={() => applyStatusFilter(opt.value)}
                    sx={{
                      flexShrink: 0,
                      borderRadius: '999px',
                      fontWeight: 500,
                      height: 30,
                      border: '1px solid',
                      borderColor: statusFilter === opt.value ? 'primary.main' : 'grey.300',
                      bgcolor: statusFilter === opt.value ? 'primary.main' : 'transparent',
                      color: statusFilter === opt.value ? 'primary.contrastText' : 'text.secondary',
                      '&:hover': {
                        bgcolor: statusFilter === opt.value ? 'primary.dark' : 'grey.100',
                      },
                    }}
                  />
                ))}
              </Box>
              <Tooltip title={t('pours.search', 'Search')}>
                <IconButton
                  size="small"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('pours.search', 'Search')}
                  sx={{ flexShrink: 0, color: searchFilter ? 'primary.main' : 'text.secondary' }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.refresh')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={loading}
                    aria-label={t('common.refresh')}
                    sx={{ flexShrink: 0, color: 'text.secondary' }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      ) : (
        <Box mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">{t('pours.statusFilter')}</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label={t('pours.statusFilter')}
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="All">{t('common.all')}</MenuItem>
              <MenuItem value="In progress">{t('pours.inProgress')}</MenuItem>
              <MenuItem value="Pending">{t('dashboard.pending')}</MenuItem>
              <MenuItem value="Complete">{t('pours.complete')}</MenuItem>
              {canManagePours && (
                <MenuItem value="Archived">{t('pours.archived', 'Archived')}</MenuItem>
              )}
            </Select>
          </FormControl>

          {userIsSuper && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="client-filter-label">{t('pours.clientFilter', 'Client')}</InputLabel>
              <Select
                labelId="client-filter-label"
                value={clientFilter}
                label={t('pours.clientFilter', 'Client')}
                onChange={(e: SelectChangeEvent) => {
                  setClientFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">{t('pours.allClients', 'All clients')}</MenuItem>
                {activeClients.length > 0 && (
                  <ListSubheader>{t('pours.clientsInProgress', 'With pours in progress')}</ListSubheader>
                )}
                {activeClients.map((c) => (
                  <MenuItem key={c.companyid} value={c.companyid}>
                    {c.companyname} ({clientCounts[c.companyid]})
                  </MenuItem>
                ))}
                {activeClients.length > 0 && otherClients.length > 0 && (
                  <ListSubheader>{t('pours.clientsOther', 'Other clients')}</ListSubheader>
                )}
                {otherClients.map((c) => (
                  <MenuItem key={c.companyid} value={c.companyid}>
                    {c.companyname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            size="small"
            sx={{ flex: 1, maxWidth: 400 }}
            variant="outlined"
            placeholder={t('pours.searchPlaceholder')}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={t('common.refresh')}>
            <span>
              <IconButton onClick={handleRefresh} disabled={loading} aria-label={t('common.refresh')}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          {canManagePours && (
            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => navigate('/pours/new')}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3, py: 1 }}
            >
              {t('pours.createPour', 'New Pour')}
            </Button>
          )}
        </Box>
      )}

      {/* Map Section */}
      <Paper
        elevation={isMobile && !showMap ? 0 : 1}
        sx={{ mb: 2, overflow: 'hidden', borderRadius: 1 }}
      >
        {isMobile ? (
          /* Faint hairline seam the map slides out of — the open/close control
             lives in the bottom nav, so this is purely visual. */
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 32, height: 3, borderRadius: 2, bgcolor: 'divider' }} />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={0.5}
            sx={{ borderBottom: showMap ? 1 : 'none', borderBottomColor: 'grey.300', backgroundColor: 'grey.50' }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title={showMap ? t('dashboard.hideMap') : t('dashboard.showMap')}>
                <IconButton size="small" onClick={() => setLocalShowMap(!localShowMap)} color="primary">
                  {showMap ? <MapIcon fontSize="small" /> : <MapOutlinedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {t('dashboard.mapView')}
              </Typography>
            </Box>
          </Box>
        )}
        <Collapse in={showMap}>
          <Box sx={{
            height: mapHeights[mapSize],
            transition: isDragging ? 'none' : 'height 0.3s ease',
            '& .leaflet-control-attribution': { display: 'none' },
            '& img.leaflet-tile': { mixBlendMode: 'unset' },
          }}>
            {!mapDataReady ? (
              <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'grey.100' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
            <MapContainer
              center={mapCenter}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              attributionControl={false}
            >
              <InvalidateSize height={mapHeights[mapSize]} />
              <FocusPosition position={focusPosition} zoom={14} />
              <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {projectPins.map(({ project: proj }) => (
                <Marker
                  key={`proj-${proj.project_id}`}
                  position={[Number(proj.latitude), Number(proj.longitude)]}
                  icon={projectIcon}
                >
                  <Popup>
                    <strong>{proj.project_name}</strong>
                    <br />
                    {proj.client && <>Client: {proj.client}<br /></>}
                    {proj.address && <>{proj.address}<br /></>}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setSearchFilter(proj.project_name);
                        }}
                        sx={{ textTransform: 'none', py: 0.25, px: 1.5, fontSize: '0.75rem', minWidth: 0 }}
                      >
                        View Pours
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/projects/${proj.project_id}/edit`)}
                        sx={{ textTransform: 'none', py: 0.25, px: 1.5, fontSize: '0.75rem', minWidth: 0 }}
                      >
                        Edit Project
                      </Button>
                    </Box>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            )}
          </Box>
          <Box
            onMouseDown={handleDragStart}
            sx={{
              height: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'row-resize', backgroundColor: 'grey.50',
              borderTop: 1, borderTopColor: 'grey.300',
              '&:hover': { backgroundColor: 'grey.100' }, userSelect: 'none',
            }}
          >
            <DragHandleIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Box>
        </Collapse>
      </Paper>

      {/* Mobile Card View */}
      {isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {pours.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>
              <EmptyState
                title={t('dashboard.noPours')}
                description={t('dashboard.noPoursDescription')}
                icon={<SearchIcon />}
                actionLabel={canManagePours ? t('pours.createPour', 'Create Pour') : undefined}
                onAction={canManagePours ? () => navigate('/pours/new') : undefined}
              />
            </Paper>
          ) : (
            pours.map((pour) => (
              <Card
                key={pour.PourID}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <CardActionArea onClick={() => handleRowClick(pour)}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {pour.PourName || '-'}
                      </Typography>
                      <Chip
                        label={pour.PourStatus ? formatStatusLabel(displayStatus(pour)) : 'Unknown'}
                        size="small"
                        sx={{ flexShrink: 0, borderRadius: '999px', fontWeight: 500, fontSize: '0.72rem', height: 22, ...getStatusPillSx(displayStatus(pour)) }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {pour.ProjectName || '-'}{pour.PourCode ? ` · ${pour.PourCode}` : ''}
                    </Typography>
                    {pourDateRange(pour) && (
                      <Typography variant="caption" color="text.disabled" display="block">
                        {pourDateRange(pour)}
                      </Typography>
                    )}
                    {renderPourHealth(pour)}
                  </CardContent>
                </CardActionArea>
                <Collapse in={expandedPourId === pour.PourID} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                    {renderExpandedContent(pour.PourID, pour)}
                  </Box>
                </Collapse>
              </Card>
            ))
          )}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      )}

      {/* Desktop Table with expandable rows */}
      {!isMobile && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('pours.pourCode')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('common.status')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('pours.project')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('pours.pourName')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('pours.product')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('pours.functionality')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>
                  {LIST_CLARITY
                    ? `${t('pours.started', 'Started')}${listTimeZone ? ` (${listTimeZone})` : ''}`
                    : t('pours.loggerStart')}
                </TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX }} align="center">
                  {LIST_CLARITY ? t('pours.age', 'Age') : t('pours.duration')}
                </TableCell>
                {showTimeZoneColumn && <TableCell sx={HEADER_CELL_SX}>{t('pours.timezone')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {pours.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography color="text.secondary">{t('common.loading')}</Typography>
                    ) : (
                      <EmptyState
                        title={t('dashboard.noPours')}
                        description={t('dashboard.noPoursDescription')}
                        icon={<SearchIcon />}
                        actionLabel={canManagePours ? t('pours.createPour', 'Create Pour') : undefined}
                        onAction={canManagePours ? () => navigate('/pours/new') : undefined}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                pours.map((pour) => (
                  <React.Fragment key={pour.PourID}>
                    <TableRow
                      hover
                      onClick={() => handleRowClick(pour)}
                      onMouseEnter={() => { if (!pour.Archived) handleRowHover(pour.PourID); }}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: expandedPourId === pour.PourID ? 'action.selected' : undefined,
                      }}
                    >
                      <TableCell>{pour.PourCode || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={pour.PourStatus ? formatStatusLabel(displayStatus(pour)) : 'Unknown'}
                          color={getStatusColor(displayStatus(pour))}
                          icon={getStatusIcon(displayStatus(pour))}
                          size="small"
                          variant="filled"
                        />
                        {renderPastPlannedEnd(pour)}
                      </TableCell>
                      <TableCell>{pour.ProjectName || '-'}</TableCell>
                      <TableCell>{pour.PourName || '-'}</TableCell>
                      <TableCell>{pour.ProductCode || '-'}</TableCell>
                      <TableCell>{functionalityLabel(pour.Functionality, pour.MonitoringFunctions)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {/* A Scheduled pour says when it starts, not "started" at a future time. */}
                        {isScheduledPour(pour)
                          ? `${t('pours.starts', 'Starts')} ${formatStartedLabel(pour.StartsAt ?? pour.LoggerStart, pour.TimeZoneIANA) || '-'}`
                          : LIST_CLARITY
                            ? (formatStartedLabel(pour.LoggerStart, pour.TimeZoneIANA) || '-')
                            : formatLoggerStartTime(pour.LoggerStart, pour.TimeZoneIANA)}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {isScheduledPour(pour)
                          ? '-'
                          : LIST_CLARITY
                            ? formatAgeLabel(pour.LoggerDuration)
                            : pour.LoggerDuration != null ? Number(pour.LoggerDuration).toFixed(1) : '-'}
                      </TableCell>
                      {showTimeZoneColumn && <TableCell>{pourZoneLabel(pour)}</TableCell>}
                    </TableRow>
                    {/* Expandable detail row */}
                    <TableRow>
                      <TableCell colSpan={totalColumns} sx={{ p: 0, borderBottom: expandedPourId === pour.PourID ? undefined : 'none' }}>
                        <Collapse in={expandedPourId === pour.PourID} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
                            {renderExpandedContent(pour.PourID, pour)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {debouncedSearch || statusFilter !== 'All'
                ? `Showing ${totalCount} of ${totalAll} pours (filtered)`
                : `Total pours: ${totalAll}`}
              {statusFilter !== 'All' && <span> &bull; Status: {formatStatusLabel(statusFilter)}</span>}
            </Typography>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              labelRowsPerPage={t('common.rowsPerPage')}
            />
          </Box>
        </TableContainer>
      )}

      {/* Snackbar-style toasts. Auto-dismiss handled by user clicking close. */}
      {stopError && (
        <Alert
          severity="error"
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}
          onClose={() => setStopError('')}
        >
          {stopError}
        </Alert>
      )}
      {stopSuccess && (
        <Alert
          severity="success"
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}
          onClose={() => setStopSuccess('')}
        >
          {stopSuccess}
        </Alert>
      )}
      {reportMessage && (
        <Alert
          severity="success"
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}
          onClose={() => setReportMessage('')}
        >
          {reportMessage}
        </Alert>
      )}

      <ReportDialog
        open={!!reportDialogPour}
        onClose={() => setReportDialogPour(null)}
        pourId={reportDialogPour?.PourID || ''}
        pourCode={reportDialogPour?.PourCode}
        onSuccess={(msg) => setReportMessage(msg)}
      />

      {/* Stop-pour confirmation dialog */}
      <Dialog
        open={stopTargetPour !== null}
        onClose={() => {
          if (stoppingPourId === null) {
            setStopTargetPour(null);
            setStopTankCount(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={stoppingPourId !== null}
      >
        <DialogTitle>{t('pours.stopConfirmTitle', 'Stop Pour')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {t('pours.stopConfirmBody', {
              code: stopTargetPour?.PourCode || stopTargetPour?.PourName || '',
            })}
          </DialogContentText>
          {stopTankCount !== null && stopTankCount > 0 && (
            <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
              {t('pours.stopConfirmTankNote', { count: stopTankCount })}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStopTargetPour(null);
              setStopTankCount(null);
            }}
            disabled={stoppingPourId !== null}
          >
            {t('pours.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleStopConfirm}
            disabled={stoppingPourId !== null}
            startIcon={
              stoppingPourId !== null ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <StopCircleIcon />
              )
            }
          >
            {stoppingPourId !== null
              ? t('pours.stopping', 'Stopping...')
              : t('pours.confirmStop', 'Stop Pour')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive pour confirmation */}
      <Dialog
        open={archiveTargetPour !== null}
        onClose={() => { if (!archivingPour) setArchiveTargetPour(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {archiveTargetPour?.PourStatus === 'Pending'
            ? t('pours.deleteConfirmTitle', 'Delete Pour')
            : t('pours.archiveConfirmTitle', 'Archive Pour')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {archiveTargetPour?.PourStatus === 'Pending'
              ? t('pours.deleteConfirmBody', {
                  defaultValue:
                    'Are you sure you want to delete "{{code}}"? No logger has been attached yet, so nothing has been recorded. It will be removed from ConXedge lists and can be restored from the Archived view if needed.',
                  code: archiveTargetPour?.PourCode || archiveTargetPour?.PourName || '',
                })
              : t('pours.archiveConfirmBody', {
                  defaultValue:
                    'Are you sure you want to archive "{{code}}"? It will be removed from ConXedge lists and dashboards. All recorded data is retained and the pour can be restored from the Archived view.',
                  code: archiveTargetPour?.PourCode || archiveTargetPour?.PourName || '',
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTargetPour(null)} disabled={archivingPour}>
            {t('pours.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleArchiveConfirm}
            disabled={archivingPour}
            startIcon={
              archivingPour
                ? <CircularProgress size={20} color="inherit" />
                : archiveTargetPour?.PourStatus === 'Pending' ? <DeleteIcon /> : <ArchiveIcon />
            }
          >
            {archiveTargetPour?.PourStatus === 'Pending'
              ? (archivingPour ? t('pours.deleting', 'Deleting...') : t('pours.confirmDelete', 'Delete Pour'))
              : (archivingPour ? t('pours.archiving', 'Archiving...') : t('pours.confirmArchive', 'Archive Pour'))}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** The Control Panel: the phone layout below 900px, else today's page. */
const Pours: React.FC = () => (useIsMobile() ? <MobileControlPanel /> : <DesktopPours />);

export default Pours;
